/**
 * REUSABLE REPORT TABLE COMPONENT
 * Displays pivoted report data with fixed columns + dynamic quarter columns + Total
 * Works for all report types (Disbursement, Repayment, Exposure)
 */

import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { DisbursementReportRow, ExposureReportRow } from '../models/report.model';
import { ReportPivotService } from '../services/report-pivot.service';

@Component({
  selector: 'app-report-table',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  template: `
    <div class="bg-[#FAFAFA] rounded-[20px] p-4 sm:p-6">
      <!-- Header with title and actions -->
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-lg sm:text-xl font-semibold text-[#444A6D]">{{ displayTitle || title }}</h2>
        <div class="flex gap-2">
          <button
            (click)="onExportCSV()"
            class="text-sm px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          >
            📥 {{ 'REPORTS.TABLE.EXPORT_EXCEL' | translate }}
          </button>
        </div>
      </div>

      <!-- Table -->
      <div class="relative overflow-x-auto w-full bg-white rounded-lg shadow">
        <table class="w-full border-collapse">
          <!-- HEADER ROW -->
          <thead>
            <tr class="bg-indigo-600 text-white sticky top-0">
              <!-- Fixed Columns -->
              <th class="px-4 py-3 text-start font-semibold sticky start-0 bg-indigo-600 z-40">
                {{ 'REPORTS.TABLE.SR_NO' | translate }}
              </th>
              <th class="px-4 py-3 text-start font-semibold sticky start-12 bg-indigo-600 z-40">
                {{ 'REPORTS.TABLE.BORROWER_NAME' | translate }}
              </th>
              <th class="px-4 py-3 text-start font-semibold relative z-0">{{ 'REPORTS.TABLE.FACILITY_TYPE' | translate }}</th>
              <th class="px-4 py-3 text-start font-semibold relative z-0">{{ 'REPORTS.TABLE.PHASE' | translate }}</th>
              <th class="px-4 py-3 text-end font-semibold relative z-0">{{ 'REPORTS.TABLE.FACILITY_AMOUNT' | translate }}</th>
              <th class="px-4 py-3 text-start font-semibold relative z-0">{{ 'REPORTS.TABLE.FACILITY_END_DATE' | translate }}</th>
              <th class="px-4 py-3 text-start font-semibold relative z-0">{{ 'REPORTS.TABLE.KIBOR_PERIOD' | translate }}</th>
              <th class="px-4 py-3 text-end font-semibold relative z-0">{{ 'REPORTS.TABLE.SPREAD_PCT' | translate }}</th>
              <th class="px-4 py-3 text-end font-semibold relative z-0">{{ 'REPORTS.TABLE.KIBOR_RATE_PCT' | translate }}</th>
              <th class="px-4 py-3 text-end font-semibold relative z-0">{{ 'REPORTS.TABLE.TOTAL_RATE_PCT' | translate }}</th>
              @if (isExposureReport()) {
                <th class="px-4 py-3 text-end font-semibold relative z-0">{{ 'REPORTS.TABLE.OPENING_EXPOSURE' | translate }}</th>
                <th class="px-4 py-3 text-end font-semibold relative z-0">{{ 'REPORTS.TABLE.DISBURSEMENT_AMOUNT' | translate }}</th>
                <th class="px-4 py-3 text-end font-semibold relative z-0">{{ 'REPORTS.TABLE.REPAYMENTS' | translate }}</th>
                <th class="px-4 py-3 text-end font-semibold relative z-0">{{ 'REPORTS.TABLE.CLOSING_EXPOSURE' | translate }}</th>
              } @else {
                <th class="px-4 py-3 text-start font-semibold relative z-0">{{ 'REPORTS.TABLE.DISBURSEMENT_DATE' | translate }}</th>
                <th class="px-4 py-3 text-end font-semibold relative z-0">{{ 'REPORTS.TABLE.DISBURSEMENT_AMOUNT' | translate }}</th>
              }
              <th class="px-4 py-3 text-end font-semibold relative z-0">{{ 'REPORTS.TABLE.MARKUP' | translate }}</th>

              <!-- Dynamic Quarter Columns -->
              @for (quarter of quarterColumns; track quarter) {
                <th
                  class="px-4 py-3 text-end font-semibold text-indigo-100 bg-indigo-700 relative z-0"
                >
                  {{ quarter }}
                </th>
              }
              @if (isExposureReport()) {
                <th class="px-4 py-3 text-end font-semibold relative z-0">{{ 'REPORTS.TABLE.REPAYMENT_AMOUNT' | translate }}</th>
              }
              <!-- Total Column (Always Last) -->
              <th
                class="px-4 py-3 text-end font-semibold sticky end-0 bg-indigo-600 z-20 border-s border-indigo-500"
              >
                {{ 'COMMON.TOTAL' | translate }}
              </th>
            </tr>
          </thead>

          <!-- DATA ROWS -->
          <tbody>
            @for (row of rows; track row.srNo; let isEven = $even) {
              <tr [class.bg-gray-50]="isEven" class="border-b border-gray-200 hover:bg-indigo-50">
                <!-- Sr No (Frozen Left) -->
                <td
                  [class.bg-gray-50]="isEven"
                  class="px-4 py-3 font-semibold text-gray-700 sticky start-0 z-30 bg-white"
                >
                  {{ row.srNo }}
                </td>

                <!-- Borrower Name (Frozen Left) -->
                <td
                  [class.bg-gray-50]="isEven"
                  class="px-4 py-3 text-gray-700 sticky start-12 z-30 bg-white"
                >
                  {{ row.borrowerName }}
                </td>

                <!-- Facility Type -->
                <td
                  [class.bg-gray-50]="isEven"
                  class="px-4 py-3 text-gray-700 relative z-0 bg-white"
                >
                  {{ row.fcltyTyp | translate }}
                </td>

                <!-- Phase -->
                <td
                  [class.bg-gray-50]="isEven"
                  class="px-4 py-3 text-gray-700 relative z-0 bg-white"
                >
                  {{ row.phsKey | translate }}
                </td>

                <!-- Facility Amount -->
                <td
                  [class.bg-gray-50]="isEven"
                  class="px-4 py-3 text-end text-gray-700 font-medium relative z-0 bg-white"
                >
                  {{ formatCurrency(row.fcltyAmount) }}
                </td>

                <!-- Facility End Date -->
                <td
                  [class.bg-gray-50]="isEven"
                  class="px-4 py-3 text-gray-700 relative z-0 bg-white"
                >
                  {{ row.fcltyEndDt }}
                </td>

                <!-- KIBOR Period -->
                <td
                  [class.bg-gray-50]="isEven"
                  class="px-4 py-3 text-gray-700 text-sm relative z-0 bg-white"
                >
                  {{ row.kbrPeriod | translate }}
                </td>

                <!-- Spread % -->
                <td
                  [class.bg-gray-50]="isEven"
                  class="px-4 py-3 text-end text-gray-700 relative z-0 bg-white"
                >
                  {{ row.spread.toFixed(2) }}%
                </td>

                <!-- KIBOR Rate % -->
                <td
                  [class.bg-gray-50]="isEven"
                  class="px-4 py-3 text-end text-gray-700 relative z-0 bg-white"
                >
                  @if (row.kiborRt !== null && row.kiborRt !== undefined) {
                    {{ row.kiborRt.toFixed(2) }}%
                  } @else {
                    -
                  }
                </td>

                <!-- Total Rate % -->
                <td
                  [class.bg-gray-50]="isEven"
                  class="px-4 py-3 text-end text-gray-700 relative z-0 bg-white"
                >
                  @if (row.totalRt !== null && row.totalRt !== undefined) {
                    {{ row.totalRt.toFixed(2) }}%
                  } @else {
                    -
                  }
                </td>

                @if (isExposureReport()) {
                  <!-- Opening Exposure -->
                  <td
                    [class.bg-gray-50]="isEven"
                    class="px-4 py-3 text-end text-gray-700 font-medium relative z-0 bg-white"
                  >
                    {{ formatCurrency(row.opening) }}
                  </td>
                  <!-- Disbursement Amount -->
                  <td
                    [class.bg-gray-50]="isEven"
                    class="px-4 py-3 text-end text-gray-700 font-medium relative z-0 bg-white"
                  >
                    @if (isExposureRow(row)) {
                      {{ formatCurrency(row.disursmentAmount) }}
                    } @else {
                      -
                    }
                  </td>
                  <!-- Repayments -->
                  <td
                    [class.bg-gray-50]="isEven"
                    class="px-4 py-3 text-end text-gray-700 font-medium relative z-0 bg-white"
                  >
                    @if (isExposureRow(row)) {
                      {{ formatCurrency(row.repaymentAmount) }}
                    } @else {
                      -
                    }
                  </td>
                  <!-- Closing Exposure -->
                  <td
                    [class.bg-gray-50]="isEven"
                    class="px-4 py-3 text-end text-gray-700 font-medium relative z-0 bg-white"
                  >
                    @if (isExposureRow(row)) {
                      {{ formatCurrency(row.closingExposure) }}
                    } @else {
                      -
                    }
                  </td>
                } @else {
                  <!-- Disbursement Date (Disbursement Report Only) -->
                  <td
                    [class.bg-gray-50]="isEven"
                    class="px-4 py-3 text-start text-gray-700 font-medium relative z-0 bg-white"
                  >
                    {{ getDisbursementDate(row) }}
                  </td>
                  <!-- Disbursement Amount (Disbursement Report Only) -->
                  <td
                    [class.bg-gray-50]="isEven"
                    class="px-4 py-3 text-end text-gray-700 font-medium relative z-0 bg-white"
                  >
                    {{ formatCurrency(getDisbursementAmount(row)) }}
                  </td>
                }

                <!-- Markup -->
                <td
                  [class.bg-gray-50]="isEven"
                  class="px-4 py-3 text-end text-gray-700 relative z-0 bg-white"
                >
                  @if (row.markup !== null && row.markup !== undefined) {
                    {{ formatCurrency(row.markup) }}
                  } @else {
                    -
                  }
                </td>

                <!-- Quarter Data (Dynamic) -->
                @for (quarter of quarterColumns; track quarter) {
                  <td
                    [class.bg-gray-50]="isEven"
                    class="px-4 py-3 text-end text-gray-700 relative z-0 bg-white"
                  >
                    {{ formatCurrency(row.quarterData[quarter] || 0) }}
                  </td>
                }
                @if (isExposureReport()) {
                  <td
                    [class.bg-gray-50]="isEven"
                    class="px-4 py-3 text-end text-gray-700 font-medium relative z-0 bg-white"
                  >
                    @if (isExposureRow(row)) {
                      {{ formatCurrency(row.repaymentAmount) }}
                    } @else {
                      -
                    }
                  </td>
                }
                <!-- Total (Frozen Right) -->
                <td
                  class="px-4 py-3 text-end font-bold text-indigo-700 sticky end-0 bg-indigo-50 z-20 border-s border-indigo-200"
                >
                  {{ formatCurrency(row.total) }}
                </td>
              </tr>
            }
          </tbody>

          <!-- FOOTER: Grand Total Row -->
          <tfoot>
            <tr class="bg-indigo-100 font-bold border-t-2 border-indigo-600">
              <!-- Fixed Columns -->
              <td class="px-4 py-3 sticky start-0 bg-indigo-100 z-30">-</td>
              <td class="px-4 py-3 sticky start-12 bg-indigo-100 z-30">{{ 'REPORTS.TABLE.GRAND_TOTAL' | translate }}</td>
              <td class="px-4 py-3 relative z-0 bg-indigo-100">-</td>
              <td class="px-4 py-3 relative z-0 bg-indigo-100">-</td>
              <td class="px-4 py-3 text-end relative z-0 bg-indigo-100">
                {{ formatCurrency(calculateTotalForColumn('fcltyAmount')) }}
              </td>
              <td class="px-4 py-3 relative z-0 bg-indigo-100">-</td>
              <td class="px-4 py-3 relative z-0 bg-indigo-100">-</td>
              <td class="px-4 py-3 relative z-0 bg-indigo-100">-</td>
              <td class="px-4 py-3 relative z-0 bg-indigo-100">-</td>
              <td class="px-4 py-3 relative z-0 bg-indigo-100">-</td>
              @if (isExposureReport()) {
                <td class="px-4 py-3 text-end relative z-0 bg-indigo-100">
                  {{ formatCurrency(calculateTotalForColumn('opening')) }}
                </td>
                <td class="px-4 py-3 text-end relative z-0 bg-indigo-100">
                  {{ formatCurrency(calculateTotalForColumn('disursmentAmount')) }}
                </td>
                <td class="px-4 py-3 text-end relative z-0 bg-indigo-100">
                  {{ formatCurrency(calculateTotalForColumn('repaymentAmount')) }}
                </td>
                <td class="px-4 py-3 text-end relative z-0 bg-indigo-100">
                  {{ formatCurrency(calculateClosingExposureTotal()) }}
                </td>
              } @else {
                <td class="px-4 py-3 relative z-0 bg-indigo-100">-</td>
                <td class="px-4 py-3 text-end relative z-0 bg-indigo-100">
                  {{ formatCurrency(calculateTotalForColumn('disursmentAmount')) }}
                </td>
              }
              <td class="px-4 py-3 text-end relative z-0 bg-indigo-100">
                {{ formatCurrency(calculateTotalForColumn('markup')) }}
              </td>

              <!-- Quarter Totals -->
              @for (quarter of quarterColumns; track quarter) {
                <td
                  class="px-4 py-3 text-end text-indigo-900 bg-indigo-100 relative z-0 bg-indigo-100"
                >
                  {{ formatCurrency(calculateQuarterTotal(quarter)) }}
                </td>
              }
              @if (isExposureReport()) {
                <td class="px-4 py-3 text-end relative z-0 bg-indigo-100">
                  {{ formatCurrency(calculateTotalForColumn('repaymentAmount')) }}
                </td>
              }
              <!-- Grand Total -->
              <td
                class="px-4 py-3 text-end sticky end-0 bg-indigo-100 z-20 border-s border-indigo-400"
              >
                {{ formatCurrency(calculateGrandTotal()) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Footer Info -->
      <div class="p-4 bg-gray-50 border-t border-gray-200 text-sm text-gray-600">
        {{ 'REPORTS.TABLE.TOTAL_RECORDS' | translate }} <span class="font-semibold">{{ rows.length }}</span>
      </div>
    </div>
  `,
  styles: [
    `
      /* Horizontal scrollbar styling */
      .overflow-x-auto::-webkit-scrollbar {
        height: 8px;
      }

      .overflow-x-auto::-webkit-scrollbar-track {
        background: #f1f5f9;
      }

      .overflow-x-auto::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 4px;
      }

      .overflow-x-auto::-webkit-scrollbar-thumb:hover {
        background: #94a3b8;
      }

      /* Table styling */
      table {
        min-width: 100%;
      }

      th,
      td {
        white-space: nowrap;
      }
    `,
  ],
})
export class ReportTableComponent implements OnChanges {
  @Input() title: string = 'Report';
  /** Optional translated title shown instead of `title` (`title` stays the raw value used in logic). */
  @Input() displayTitle = '';
  @Input() rows: (DisbursementReportRow | ExposureReportRow)[] = [];
  @Input() quarterColumns: string[] = [];
  @Input() startDate: Date | string | null = null;
  @Input() endDate: Date | string | null = null;

  @Output() exportCSV = new EventEmitter<void>();

  constructor(private pivotService: ReportPivotService) { }

  ngOnChanges(changes: SimpleChanges): void {
    // Ensure columns are sorted
    if (changes['quarterColumns'] && this.quarterColumns.length > 0) {
      this.quarterColumns.sort((a, b) => this.compareQuarters(a, b));
    }
  }

  /**
   * Format number as currency with 2 decimal places for precision
   */
  formatCurrency(value: number | null | undefined): string {
    if (value == null) {
      return '0';
    }
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  /**
   * Calculate total for a column
   */
  calculateTotalForColumn(
    field: 'fcltyAmount' | 'opening' | 'disursmentAmount' | 'repaymentAmount' | 'markup',
  ): number {
    return this.rows.reduce((sum, row) => {
      let val: number | undefined;
      if (field === 'disursmentAmount' && 'disursmentAmount' in row) {
        val = (row as DisbursementReportRow).disursmentAmount;
      } else if (field === 'repaymentAmount' && 'repaymentAmount' in row) {
        val = (row as ExposureReportRow).repaymentAmount;
      } else {
        val = (row as any)[field];
      }
      return sum + (val || 0);
    }, 0);
  }

  /**
   * Calculate total for a quarter across all rows
   */
  calculateQuarterTotal(quarter: string): number {
    return this.rows.reduce((sum, row) => sum + (row.quarterData[quarter] || 0), 0);
  }

  /**
   * Calculate total closing exposure across all rows (only for Exposure Report)
   */
  calculateClosingExposureTotal(): number {
    return this.rows.reduce((sum, row) => {
      if ('closingExposure' in row) {
        return sum + ((row as ExposureReportRow).closingExposure || 0);
      }
      return sum;
    }, 0);
  }

  /**
   * Calculate grand total
   */
  calculateGrandTotal(): number {
    return this.rows.reduce((sum, row) => sum + row.total, 0);
  }

  /**
   * Get disbursement date for Disbursement Report rows
   */
  getDisbursementDate(row: DisbursementReportRow | ExposureReportRow): string {
    if ('dsbmntDt' in row) {
      return (row as DisbursementReportRow).dsbmntDt || '-';
    }
    return '-';
  }

  /**
   * Get disbursement amount for Disbursement Report rows
   */
  getDisbursementAmount(row: DisbursementReportRow | ExposureReportRow): number {
    if ('dsbmntDt' in row) {
      return (row as DisbursementReportRow).disursmentAmount || 0;
    }
    return 0;
  }

  /**
   * Type guard: check if row is an Exposure Report row
   */
  isExposureRow(row: DisbursementReportRow | ExposureReportRow): row is ExposureReportRow {
    return 'repaymentAmount' in row && 'closingExposure' in row;
  }

  /**
   * Check if this is an Exposure Report based on title
   */
  isExposureReport(): boolean {
    return this.title.toLowerCase().includes('exposure');
  }

  /**
   * Export table as Excel
   */
  onExportCSV(): void {
    const isExposureReportFlag = this.isExposureReport();

    const filename = isExposureReportFlag
      ? `exposure-report-${new Date().toISOString().split('T')[0]}.xlsx`
      : `disbursement-report-${new Date().toISOString().split('T')[0]}.xlsx`;

    console.log('Exposure export dates:', {
      startDate: this.startDate,
      endDate: this.endDate,
    });

    this.pivotService.exportToCSV(
      this.rows,
      this.quarterColumns,
      filename,
      isExposureReportFlag,
      this.startDate,
      this.endDate,
    );

    this.exportCSV.emit();
  }

  /**
   * Compare quarters for sorting
   */
  private compareQuarters(a: string, b: string): number {
    const monthMap: { [key: string]: number } = {
      Jan: 1,
      Feb: 2,
      Mar: 3,
      Apr: 4,
      May: 5,
      Jun: 6,
      Jul: 7,
      Aug: 8,
      Sep: 9,
      Oct: 10,
      Nov: 11,
      Dec: 12,
    };

    const [aMonth, aYear] = a.split('-');
    const [bMonth, bYear] = b.split('-');

    const aYearNum = parseInt(aYear, 10);
    const bYearNum = parseInt(bYear, 10);

    if (aYearNum !== bYearNum) {
      return aYearNum - bYearNum;
    }

    return (monthMap[aMonth] || 0) - (monthMap[bMonth] || 0);
  }
}
